import * as React from 'react';
import useSWR from 'swr';
import { isAxiosError } from '@mentimeter/http-clients';
import { useSearchParams } from '@mentimeter/next-navigation';
import { useAppDispatch } from '../redux-hooks';
import { refreshSeriesSuccess } from '../reducers/seriesSlice';
import { useAuthenticationStore } from '../features/auth/authenticationStore';
import { getSlideDeckByKey } from '../api/slideDeck';

interface AuthenticationInstructionsResponse {
  message: string;
  series: { identity_mode: string; identity_visibility: string };
  redirect_url: string;
}

const hardReloadPageTimeout = process.env.NEXT_PUBLIC_HARD_RELOAD_PAGE_MS
  ? Number.parseInt(process.env.NEXT_PUBLIC_HARD_RELOAD_PAGE_MS, 10)
  : 1000 * 60 * 60 * 24;

const createdAt = Date.now();

function toQueryParam(from: any): string {
  if (from === null) {
    return '';
  } else if (typeof from === 'object') {
    if (Array.isArray(from)) {
      return from.join(',');
    } else {
      return JSON.stringify(from);
    }
  } else {
    return String(from);
  }
}

export function useRefreshSeries(voteKey: string, enableAutoRefresh = true) {
  const dispatch = useAppDispatch();
  const {
    setIdentityMode,
    setIdentityVisibility,
    setAuthenticationNeeded,
    setRedirectUrl,
  } = useAuthenticationStore();
  const searchParams = useSearchParams();
  const source = searchParams.get('source');
  const questionIndex = Number(searchParams.get('questionIndex'));

  const queryParams = new URLSearchParams();
  if (source) {
    queryParams.set('source', toQueryParam(source));
  }
  // If questionIndex is 0 we don't want to set it as a query param since this will
  // be the case for all non-survey presentations where the query param is not present
  // (since casting Number(null) === 0 as is done above). Also, this param is not present
  // on the first question of a survey presentation, so it is not needed then either.
  if (questionIndex > 0) {
    queryParams.set('question_index', toQueryParam(questionIndex));
  }

  const { mutate } = useSWR(
    `/slide-deck/${voteKey}?${queryParams.toString()}`,
    async () => {
      try {
        const seriesOrSlideDeck = await getSlideDeckByKey(voteKey, queryParams);
        setAuthenticationNeeded(false);
        dispatch(refreshSeriesSuccess(seriesOrSlideDeck));
        return seriesOrSlideDeck;
      } catch (e: unknown) {
        if (
          isAxiosError<AuthenticationInstructionsResponse>(e) &&
          e.response?.status === 401 &&
          e.response.data.message === 'Authentication is required'
        ) {
          const data = e.response.data;
          setAuthenticationNeeded(true);
          setIdentityMode(data.series.identity_mode);
          setIdentityVisibility(data.series.identity_visibility);
          setRedirectUrl(data.redirect_url);
        }

        // Ignore fails on refresh
        // TODO: Does it make sense to act on errors here?
        return;
      }
    },
    {
      refreshInterval: 0,
      revalidateOnFocus: enableAutoRefresh,
      revalidateOnReconnect: enableAutoRefresh,
    },
  );

  return React.useCallback(() => {
    const isExpired =
      createdAt > 0 && Date.now() - createdAt > hardReloadPageTimeout;
    if (isExpired) window.location.reload();
    else mutate();
  }, [mutate]);
}
